/**
 * OCTOPUS Dashboard - FASE 3: Widget Library
 * CompassRose - Rosa dos ventos para heading e curso
 * 
 * Estilo: Marítimo/Industrial moderno, tema dark
 */

import React, { useMemo } from 'react';

const CompassRose = ({
  value = 0, // ângulo em graus (0-360)
  label = '',
  showLabel = true,
  showDegrees = true,
  size = 200,
}) => {
  // Normalizar o valor para 0-360
  const normalizedValue = useMemo(() => {
    let angle = value % 360;
    if (angle < 0) angle += 360;
    return angle;
  }, [value]);

  // Converter para direção cardeal
  const cardinalDirection = useMemo(() => {
    const directions = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
    const index = Math.round(normalizedValue / 45) % 8;
    return directions[index];
  }, [normalizedValue]);

  // Gerar marcações
  const ticks = useMemo(() => {
    const elements = [];
    const centerX = size / 2;
    const centerY = size / 2;
    const outerRadius = size * 0.42;
    const innerRadius = size * 0.36;
    const labelRadius = size * 0.30;
    
    const cardinals = ['N', 'E', 'S', 'W'];
    const intercardinals = ['NE', 'SE', 'SW', 'NW'];
    
    // Marcações principais (cada 10 graus)
    for (let i = 0; i < 36; i++) {
      const angle = i * 10;
      const rad = (angle - 90) * Math.PI / 180;
      
      const isCardinal = angle % 90 === 0;
      const isIntercardinal = angle % 45 === 0 && !isCardinal;
      
      const tickInner = isCardinal ? innerRadius - 8 : (isIntercardinal ? innerRadius - 4 : innerRadius);
      
      const x1 = centerX + tickInner * Math.cos(rad);
      const y1 = centerY + tickInner * Math.sin(rad);
      const x2 = centerX + outerRadius * Math.cos(rad);
      const y2 = centerY + outerRadius * Math.sin(rad);
      
      elements.push(
        <line
          key={`tick-${i}`}
          x1={x1}
          y1={y1}
          x2={x2}
          y2={y2}
          stroke={isCardinal ? '#f3f4f6' : (isIntercardinal ? '#9ca3af' : '#4b5563')}
          strokeWidth={isCardinal ? 2.5 : (isIntercardinal ? 1.5 : 1)}
        />
      );
      
      // Labels dos pontos cardeais e intercardeais
      if (isCardinal || isIntercardinal) {
        const labelX = centerX + labelRadius * Math.cos(rad);
        const labelY = centerY + labelRadius * Math.sin(rad);
        const text = isCardinal ? cardinals[angle / 90] : intercardinals[(angle - 45) / 90];
        
        elements.push(
          <text
            key={`label-${i}`}
            x={labelX}
            y={labelY}
            fill={isCardinal ? '#f3f4f6' : '#9ca3af'}
            fontSize={isCardinal ? size * 0.08 : size * 0.055}
            fontWeight={isCardinal ? 'bold' : 'normal'}
            textAnchor="middle"
            dominantBaseline="middle"
          >
            {text}
          </text>
        );
      }
    }
    
    return elements;
  }, [size]);

  // Ponteiro/agulha
  const needle = useMemo(() => {
    const centerX = size / 2;
    const centerY = size / 2;
    const rad = (normalizedValue - 90) * Math.PI / 180;
    const length = size * 0.35;
    const baseWidth = size * 0.03;
    
    // Ponta da agulha
    const tipX = centerX + length * Math.cos(rad);
    const tipY = centerY + length * Math.sin(rad);
    
    // Cauda da agulha (oposta)
    const tailLength = size * 0.12;
    const tailX = centerX - tailLength * Math.cos(rad);
    const tailY = centerY - tailLength * Math.sin(rad);
    
    // Base da agulha (perpendicular)
    const perpRad = rad + Math.PI / 2;
    const base1X = centerX + baseWidth * Math.cos(perpRad);
    const base1Y = centerY + baseWidth * Math.sin(perpRad);
    const base2X = centerX - baseWidth * Math.cos(perpRad);
    const base2Y = centerY - baseWidth * Math.sin(perpRad);
    
    return (
      <g>
        {/* Parte vermelha (norte/frente) */}
        <polygon
          points={`${tipX},${tipY} ${base1X},${base1Y} ${base2X},${base2Y}`}
          fill="#ef4444"
          filter="drop-shadow(0 2px 4px rgba(0,0,0,0.5))"
        />
        {/* Parte branca (sul/trás) */}
        <polygon
          points={`${tailX},${tailY} ${base1X},${base1Y} ${base2X},${base2Y}`}
          fill="#f3f4f6"
          filter="drop-shadow(0 2px 4px rgba(0,0,0,0.5))"
        />
      </g>
    );
  }, [normalizedValue, size]);

  return (
    <div className="flex flex-col items-center">
      {/* Label ACIMA do compasso */}
      {showLabel && label && (
        <div 
          className="text-gray-300 font-medium text-center mb-1 truncate"
          style={{ 
            fontSize: Math.max(12, size * 0.07),
            maxWidth: size * 1.1,
          }}
          title={label}
        >
          {label}
        </div>
      )}
      
      {/* Compasso */}
      <div 
        className="flex items-center justify-center"
        style={{ width: size, height: size }}
      >
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          {/* Fundo externo */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={size * 0.46}
            fill="#111827"
            stroke="#374151"
            strokeWidth={2}
          />
          
          {/* Anel da rosa */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={size * 0.42}
            fill="none"
            stroke="#1f2937"
            strokeWidth={size * 0.12}
          />
          
          {/* Marcações e labels */}
          {ticks}
          
          {/* Círculo central */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={size * 0.08}
            fill="#1f2937"
            stroke="#374151"
            strokeWidth={1}
          />
          
          {/* Agulha */}
          {needle}
          
          {/* Centro da agulha */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={size * 0.025}
            fill="#374151"
          />
        </svg>
      </div>
      
      {/* Display digital do valor - ABAIXO do dial */}
      {showDegrees && (
        <div className="flex items-baseline gap-1 mt-1">
          <span 
            className="font-mono font-bold text-white"
            style={{ 
              fontSize: Math.max(14, size * 0.12),
              textShadow: '0 0 10px rgba(255,255,255,0.3)'
            }}
          >
            {Math.round(normalizedValue)}°
          </span>
          <span 
            className="font-bold"
            style={{ 
              fontSize: Math.max(11, size * 0.08),
              color: '#22c55e'
            }}
          >
            {cardinalDirection}
          </span>
        </div>
      )}
    </div>
  );
};

export default CompassRose;
